import { useQuery } from '@tanstack/react-query';
import httpClient, { ApiResponse } from '../httpClient';
import { BalanceResponse, UserAccount } from '../types';
import { API_ENDPOINTS } from '../endpoints';

// Get current balance and account info with margin details
export const useGetBalance = (tradingAccountId?: string) => {
    return useQuery<BalanceResponse>({
        queryKey: [API_ENDPOINTS.TRADER.BALANCE.ME, tradingAccountId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<BalanceResponse>>(
                API_ENDPOINTS.TRADER.BALANCE.ME,
                { params: tradingAccountId ? { trading_account_id: tradingAccountId } : {} }
            );
            return data.data;
        },
        refetchInterval: 10000, // Refresh every 10 seconds for real-time balance
        enabled: !!tradingAccountId, // Only fetch when trading account is selected
    });
};

// Get detailed balance information
export const useGetBalanceDetails = (tradingAccountId?: string) => {
    return useQuery<BalanceResponse>({
        queryKey: [API_ENDPOINTS.TRADER.BALANCE.DETAILS, tradingAccountId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<BalanceResponse>>(
                API_ENDPOINTS.TRADER.BALANCE.DETAILS,
                { params: tradingAccountId ? { trading_account_id: tradingAccountId } : {} }
            );
            return data.data;
        },
        refetchInterval: 15000,
        enabled: !!tradingAccountId,
    });
};

// Get balance history
export const useGetBalanceHistory = (tradingAccountId?: string, period?: string) => {
    return useQuery<
        Array<{
            date: string;
            balance: number;
            equity: number;
            unrealized_pnl: number;
            daily_change: number;
        }>
    >({
        queryKey: [API_ENDPOINTS.TRADER.BALANCE.HISTORY, tradingAccountId, period],
        queryFn: async () => {
            const params: any = {};
            if (tradingAccountId) params.trading_account_id = tradingAccountId;
            if (period) params.period = period;

            const { data } = await httpClient.get<ApiResponse<any>>(
                API_ENDPOINTS.TRADER.BALANCE.HISTORY,
                { params }
            );
            return data.data;
        },
        refetchInterval: 30000,
        enabled: !!tradingAccountId,
    });
};

// Get PnL breakdown
export const useGetPnLBreakdown = (tradingAccountId?: string, period?: string) => {
    return useQuery<{
        period: string;
        total_pnl: number;
        realized_pnl: number;
        unrealized_pnl: number;
        by_instrument: Array<{
            instrument: string;
            realized_pnl: number;
            unrealized_pnl: number;
            total_pnl: number;
        }>;
        by_day: Array<{
            date: string;
            pnl: number;
            realized: number;
            unrealized: number;
        }>;
        currency: string;
    }>({
        queryKey: [API_ENDPOINTS.TRADER.BALANCE.PNL, tradingAccountId, period],
        queryFn: async () => {
            const params: any = {};
            if (tradingAccountId) params.trading_account_id = tradingAccountId;
            if (period) params.period = period;

            const { data } = await httpClient.get<ApiResponse<any>>(
                API_ENDPOINTS.TRADER.BALANCE.PNL,
                { params }
            );
            return data.data;
        },
        refetchInterval: 30000,
        enabled: !!tradingAccountId,
    });
};

// Get margin information
export const useGetMarginInfo = (tradingAccountId?: string) => {
    return useQuery<{
        margin_level: number;
        margin_call_level: number;
        liquidation_level: number;
        used_margin: number;
        free_margin: number;
        is_margin_call: boolean;
        is_liquidation_risk: boolean;
        positions_at_risk: number;
    }>({
        queryKey: [API_ENDPOINTS.TRADER.BALANCE.MARGIN, tradingAccountId],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<any>>(
                API_ENDPOINTS.TRADER.BALANCE.MARGIN,
                { params: tradingAccountId ? { trading_account_id: tradingAccountId } : {} }
            );
            return data.data;
        },
        refetchInterval: 15000, // Reduced from 5s — balance events not on WebSocket yet
        enabled: !!tradingAccountId,
    });
};

// Get wallet balances (individual asset balances)
export const useGetWalletBalances = () => {
    return useQuery<
        Array<{
            asset_id: string;
            asset: {
                id: string;
                symbol: string;
                name: string;
                type: string;
            };
            balance: number;
            available_balance: number;
            frozen_balance: number;
            value_in_base_currency: number;
            percentage_of_total: number;
        }>
    >({
        queryKey: [API_ENDPOINTS.TRADER.BALANCE.WALLETS],
        queryFn: async () => {
            const { data } = await httpClient.get<ApiResponse<any>>(
                API_ENDPOINTS.TRADER.BALANCE.WALLETS
            );
            return data.data;
        },
        refetchInterval: 15000,
    });
};

// Get account summary with enhanced metrics
export const useGetAccountSummary = (tradingAccountId?: string) => {
    return useQuery<{
        account: UserAccount;
        trading_metrics: {
            total_trades: number;
            winning_trades: number;
            losing_trades: number;
            win_rate: number;
            profit_factor: number;
            average_win: number;
            average_loss: number;
            largest_win: number;
            largest_loss: number;
        };
        risk_metrics: {
            max_drawdown: number;
            current_drawdown: number;
            margin_utilization: number;
            concentration_risk: number;
            daily_var: number;
        };
        performance: {
            total_return: number;
            annualized_return: number;
            sharpe_ratio: number;
            monthly_returns: Array<{
                month: string;
                return: number;
            }>;
        };
    }>({
        queryKey: ['account-summary', tradingAccountId],
        queryFn: async () => {
            // This would combine multiple endpoints for a comprehensive view
            const params = tradingAccountId ? { trading_account_id: tradingAccountId } : {};

            const balance = await httpClient.get<ApiResponse<BalanceResponse>>(
                API_ENDPOINTS.TRADER.BALANCE.DETAILS,
                { params }
            );
            const pnl = await httpClient.get<ApiResponse<any>>(API_ENDPOINTS.TRADER.BALANCE.PNL, {
                params: { ...params, period: '1y' },
            });
            const margin = await httpClient.get<ApiResponse<any>>(
                API_ENDPOINTS.TRADER.BALANCE.MARGIN,
                { params }
            );

            // Combine the responses into a summary object
            return {
                account: balance.data.data.account,
                trading_metrics: {
                    total_trades: 0, // Would calculate from trades API
                    winning_trades: 0,
                    losing_trades: 0,
                    win_rate: 0,
                    profit_factor: 0,
                    average_win: 0,
                    average_loss: 0,
                    largest_win: 0,
                    largest_loss: 0,
                },
                risk_metrics: {
                    max_drawdown: 0,
                    current_drawdown: 0,
                    margin_utilization:
                        (margin.data.data.used_margin / balance.data.data.account.balance) * 100,
                    concentration_risk: 0,
                    daily_var: 0,
                },
                performance: {
                    total_return: 0,
                    annualized_return: 0,
                    sharpe_ratio: 0,
                    monthly_returns: [],
                },
            };
        },
        refetchInterval: 60000, // Update every minute
        enabled: !!tradingAccountId,
    });
};

// Legacy support for backward compatibility
export const useBalance = (tradingAccountId?: string) => {
    const { data, ...rest } = useGetBalance(tradingAccountId);

    // Transform new format to legacy format
    const legacyData = data
        ? {
              data: {
                  balance: data.summary.total_balance,
                  currency: data.summary.base_currency,
              },
          }
        : undefined;

    return { data: legacyData, ...rest };
};
